import {
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	Button,
	Input,
	Textarea,
	Badge,
	Switch,
} from "@/components/primitives";
import {
	ArrowRight,
	Edit,
	Plus,
	RefreshCw,
	Loader2,
	AlertTriangle,
} from "lucide-react";

export interface ContributingFactor {
	id: string;
	title: string;
	description: string;
	selected: boolean;
	isEditing?: boolean;
}

interface ContributingFactorsStepProps {
	/**
	 * List of contributing factors
	 */
	factors: ContributingFactor[];
	/**
	 * Whether factors are being loaded
	 */
	isLoading: boolean;
	/**
	 * Callback when a factor is updated
	 */
	onUpdateFactor: (id: string, field: string, value: any) => void;
	/**
	 * Callback when a factor is toggled
	 */
	onToggleFactor: (id: string) => void;
	/**
	 * Callback to generate more suggestions
	 */
	onGenerateMore: () => void;
	/**
	 * Callback to add new factor
	 */
	onAddNew: () => void;
	/**
	 * Callback when user clicks "Done"
	 */
	onContinue: () => void;
}

/**
 * Helper to count selected factors
 */
function getSelectedCount(factors: ContributingFactor[]): number {
	return factors.filter((f) => f.selected).length;
}

export function ContributingFactorsStep({
	factors,
	isLoading,
	onUpdateFactor,
	onToggleFactor,
	onGenerateMore,
	onAddNew,
	onContinue,
}: ContributingFactorsStepProps) {
	const selectedCount = getSelectedCount(factors);
	const maxSelected = 3;
	const isDisabled = selectedCount === 0;

	return (
		<>
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-orange-600">
						<AlertTriangle className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Your suggested contributing factors
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							Press the toggle switch to select up to {maxSelected} contributing
							factors which you relate to most
							<br />
							(feel free to edit or create your own)
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-4">
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
							<span className="text-muted-foreground">
								Generating contributing factors...
							</span>
						</div>
					) : (
						<>
							{/* Factors List */}
							{factors.map((factor) => (
								<div
									key={factor.id}
									className={`border rounded-lg p-3 sm:p-4 ${factor.selected ? "border-green-500" : "border-border"}`}
								>
									{/* Header Row: Title and Actions */}
									<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-2">
										<div className="flex-1 mb-3 sm:mb-0">
											{factor.isEditing ? (
												<Input
													value={factor.title}
													onChange={(e) =>
														onUpdateFactor(factor.id, "title", e.target.value)
													}
													placeholder="Factor title"
													className="mb-2"
												/>
											) : (
												<h4 className="font-medium text-base sm:text-sm">
													{factor.title}
												</h4>
											)}
										</div>

										{/* Edit and Select Buttons */}
										<div className="flex items-center justify-end gap-3 sm:gap-2 sm:ml-4">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													onUpdateFactor(
														factor.id,
														"isEditing",
														!factor.isEditing,
													)
												}
												className="h-8 w-8 sm:h-auto sm:w-auto p-2 sm:p-1"
												title={
													factor.isEditing ? "Done editing" : "Edit factor"
												}
											>
												<Edit className="h-4 w-4" />
											</Button>
											<Switch
												checked={factor.selected}
												onCheckedChange={() => onToggleFactor(factor.id)}
												disabled={
													!factor.selected && selectedCount >= maxSelected
												}
												className="scale-110 sm:scale-100"
											/>
										</div>
									</div>

									{/* Description */}
									{factor.isEditing ? (
										<Textarea
											value={factor.description}
											onChange={(e) =>
												onUpdateFactor(factor.id, "description", e.target.value)
											}
											placeholder="Factor description"
											className="min-h-[80px]"
										/>
									) : (
										<p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
											{factor.description}
										</p>
									)}
								</div>
							))}

							{/* Action Buttons */}
							<div className="flex flex-col sm:flex-row gap-3 sm:gap-2 pt-2">
								<Button
									variant="outline"
									onClick={onGenerateMore}
									disabled={isLoading}
									className="w-full sm:w-auto"
								>
									<RefreshCw className="h-4 w-4 mr-2" />
									Generate more suggestions
								</Button>
								<Button
									variant="outline"
									onClick={onAddNew}
									className="w-full sm:w-auto"
								>
									<Plus className="h-4 w-4 mr-2" />
									Add new
								</Button>
							</div>

							{/* Selection Count and Continue Button */}
							<div className="flex flex-col sm:flex-row items-center justify-between pt-4 gap-3 sm:gap-0">
								<Badge variant="outline" className="text-center">
									{selectedCount} of {maxSelected} selected
								</Badge>
								<Button
									onClick={onContinue}
									disabled={isDisabled}
									className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50"
								>
									Done
									<ArrowRight className="h-4 w-4 ml-2" />
								</Button>
							</div>
						</>
					)}
				</div>
			</CardContent>
		</>
	);
}
